import React from "react";

import {
  BROKER_ALERT_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
  BROKER_FIELD_DESC_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { ArrowRightIcon } from "@/components/ui/arrow-right-animated";
import { Card, CardContent } from "@/components/ui/card";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  CUSTOM_CSV_BROKER_SELECT_TRIGGER_CLASS,
  CUSTOM_CSV_FIELD_CARD_CLASS,
  CUSTOM_CSV_FIELD_CARD_CONTENT_CLASS,
  CUSTOM_CSV_SELECT_CONTENT_PROPS,
} from "../customCsvWizardUi";
import type { ColumnMapping, CsvImportMappableField } from "../../../../types/customCsvImport";
import {
  CUSTOM_CSV_UNMAP_COLUMN_VALUE,
  formatColumnPickerLabel,
} from "../../../../utils/customCsvImport";

export interface ColumnMappingDiagramProps {
  fileHeaders: string[];
  mappableFields: CsvImportMappableField[];
  columnMapping: ColumnMapping;
  highlightFields?: Set<string>;
  requiredFields?: Set<string>;
  /** Pass empty string or unmap sentinel to clear. */
  onMappingChange: (field: string, header: string) => void;
}

function mappingCardClass({
  isMapped,
  isRequired,
}: {
  isMapped: boolean;
  isRequired: boolean;
}): string {
  if (isMapped) {
    return "border-[color:color-mix(in_oklab,var(--brand-tropical-surf)_35%,transparent)] bg-[color:color-mix(in_oklab,var(--brand-tropical-surf)_8%,transparent)]";
  }
  if (isRequired) {
    return "border-amber-500/40 bg-amber-500/5";
  }
  return "";
}

/** Exit timestamp and hold duration are alternatives — neither is preferred. */
function isExitHoldAlternative(fieldName: string): boolean {
  return fieldName === "exit_time" || fieldName === "held_duration";
}

const COLUMN_MAPPING_CARD_CONTENT_CLASS = CUSTOM_CSV_FIELD_CARD_CONTENT_CLASS;

const ColumnMappingDiagram = ({
  fileHeaders,
  mappableFields,
  columnMapping,
  highlightFields,
  requiredFields,
  onMappingChange,
}: ColumnMappingDiagramProps): React.ReactElement => {
  if (!mappableFields.length) {
    return (
      <p className={cn(BROKER_ALERT_DESC_CLASS, "m-0")}>
        No fields to map for this row layout.
      </p>
    );
  }

  if (!fileHeaders.length) {
    return (
      <p className={cn(BROKER_ALERT_DESC_CLASS, "m-0")}>
        Upload a CSV first so we can list your file&apos;s column names.
      </p>
    );
  }

  const showExitHoldOr =
    mappableFields.some((f) => f.field === "exit_time") &&
    mappableFields.some((f) => f.field === "held_duration");

  return (
    <div className="flex flex-col gap-3" aria-label="Column mapping">
      {mappableFields.map((field, index) => {
        const mappedHeader = columnMapping[field.field]?.trim() ?? "";
        const isMapped = Boolean(mappedHeader);
        const isHighlighted = highlightFields?.has(field.field) ?? false;
        const isExitHold = isExitHoldAlternative(field.field);
        // Either exit timestamp or hold duration satisfies the requirement — no * / optional / recommended.
        const isRequired = isExitHold
          ? false
          : requiredFields?.has(field.field) ?? Boolean(field.required);
        const selectId = `custom-csv-column-map-${field.field}`;
        const fileCardClass = mappingCardClass({ isMapped, isRequired });
        const prevField = index > 0 ? mappableFields[index - 1] : null;
        const insertOrBefore =
          showExitHoldOr &&
          isExitHold &&
          prevField != null &&
          isExitHoldAlternative(prevField.field) &&
          prevField.field !== field.field;

        return (
          <React.Fragment key={field.field}>
            {insertOrBefore ? (
              <p
                className={cn(BROKER_ALERT_TITLE_CLASS, "m-0 text-center")}
                aria-hidden
              >
                OR
              </p>
            ) : null}
            <div className="flex flex-wrap items-center gap-3">
              <Card
                data-custom-csv-column-mapping-card=""
                data-mapping-side="file"
                className={cn(CUSTOM_CSV_FIELD_CARD_CLASS, "min-w-[180px] flex-1", fileCardClass)}
              >
                <CardContent className={COLUMN_MAPPING_CARD_CONTENT_CLASS}>
                  <Field className="gap-2">
                    <FieldLabel htmlFor={selectId} className={BROKER_ALERT_TITLE_CLASS}>
                      Your file
                    </FieldLabel>
                    <Select
                      value={mappedHeader || CUSTOM_CSV_UNMAP_COLUMN_VALUE}
                      onValueChange={(value) =>
                        onMappingChange(
                          field.field,
                          value === CUSTOM_CSV_UNMAP_COLUMN_VALUE ? "" : value
                        )
                      }
                    >
                      <SelectTrigger id={selectId} className={CUSTOM_CSV_BROKER_SELECT_TRIGGER_CLASS}>
                        <SelectValue placeholder="Select column…">
                          {mappedHeader || "Not mapped"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent {...CUSTOM_CSV_SELECT_CONTENT_PROPS}>
                        <SelectItem value={CUSTOM_CSV_UNMAP_COLUMN_VALUE}>
                          Not mapped
                        </SelectItem>
                        {fileHeaders.map((header) => (
                          <SelectItem key={header} value={header}>
                            {formatColumnPickerLabel(
                              header,
                              field.field,
                              columnMapping,
                              mappableFields
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </CardContent>
              </Card>

              <ArrowRightIcon
                size={20}
                className="shrink-0 text-[color:var(--brand-tropical-surf)]"
                aria-hidden
              />

              <Card
                data-custom-csv-column-mapping-card=""
                data-mapping-side="tessera"
                className={cn(CUSTOM_CSV_FIELD_CARD_CLASS, "min-w-[180px] flex-1")}
              >
                <CardContent className={COLUMN_MAPPING_CARD_CONTENT_CLASS}>
                  <Field className="gap-1">
                    <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Tessera field</FieldLabel>
                    <p className={cn(BROKER_FIELD_DESC_CLASS, "m-0")}>
                      {field.label || field.field}
                      {isRequired ? (
                        <span className="text-destructive" aria-label="required">
                          {" "}
                          *
                        </span>
                      ) : null}
                      {!isExitHold && isHighlighted && !isRequired ? (
                        <span className="text-gray-500 dark:text-white/60"> (recommended)</span>
                      ) : null}
                    </p>
                  </Field>
                </CardContent>
              </Card>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default ColumnMappingDiagram;
